/**
 * UI messages and the member's language (plan §8.10, owner i18n-metadata).
 *
 * - `t('views.filter.add')` — keys are typed (`src/i18n/_keys.gen.ts`, generated from the English
 *   catalogs): an unknown key is a type error, and so is a missing or misspelt placeholder.
 *   Template keys (`t(\`dm.cat.${c}\`)`) must match at least one key.
 * - Messages are ICU MessageFormat: `{count, plural, one {# record} other {# records}}`,
 *   `{kind, select, …}`. They are compiled at build time (Vite plugin), so the browser ships no
 *   parser; plain and `{name}` messages need no formatter at all.
 * - Catalogs live in `src/i18n/<area>.ts` (`{ en: {...}, de: {...} }`, one file per area). English
 *   loads with the app and is the fallback for every missing message; other languages load on
 *   demand (one chunk each) before the first screen in that language renders.
 * - A deferred catalog (`export const deferred = true`) loads with its screens: the area's
 *   `messages.ts` runs `await loadMessages('<area>')` at its top level and every module that uses the
 *   area's keys imports it, so no screen of the area renders before its messages are there
 *   (test/i18n-deferred.test.ts checks the imports). This keeps the first paint of `/` small (§12).
 * - The language is the member's own choice, else the workspace default (`GET /me` → `formats`,
 *   applied by `applyFormats` before an authenticated page renders; `localeReady()` resolves when
 *   its messages are loaded). Before sign-in the last applied language is used (localStorage),
 *   else the browser language. `<html lang>` always follows the language.
 *
 * Metadata labels (objects, fields, options, stages …) are not messages: the server resolves them
 * for the member's language (LocalizedText).
 */
import IntlMessageFormat from 'intl-messageformat';
import {
  type DateFormat,
  type EffectiveFormatsDTO,
  type Locale,
  type MessageVars,
  type NumberFormat,
  type ShortcutPlatform,
  formatShortcut as formatShortcutFor,
  inSentence as inSentenceFor,
  shortcutKeys as shortcutKeysFor,
} from '@dataroom/core';
import { setExprLocale } from '@dataroom/expr/locale';
import en from 'virtual:dr-i18n/en';
import areaLoaders from 'virtual:dr-i18n/areas';
import type { MessageKey, MessageParams } from '../i18n/_keys.gen';

export type { MessageKey, MessageParams };

type Compiled = (typeof en)[string];
type Catalog = Record<string, Compiled>;

const loaders: Record<Exclude<Locale, 'en'>, () => Promise<{ default: Catalog }>> = {
  de: () => import('virtual:dr-i18n/de'),
};

const catalogs: Partial<Record<Locale, Catalog>> = { en };
const pending = new Map<Locale, Promise<void>>();
/** Messages of the deferred areas loaded so far, by language (merged; keys are unique per area). */
const areaCatalogs: Partial<Record<Locale, Catalog>> = {};
const areaPending = new Map<string, Promise<void>>();
/** Deferred areas a module asked for: a language switch loads them in the new language too. */
const requestedAreas = new Set<string>();

function messageOf(lang: Locale, key: string): Compiled | undefined {
  return catalogs[lang]?.[key] ?? areaCatalogs[lang]?.[key];
}

function loadArea(area: string, lang: Locale): Promise<void> {
  const id = `${area}\u0000${lang}`;
  let p = areaPending.get(id);
  if (!p) {
    const load = areaLoaders[area]?.[lang];
    if (!load) return Promise.reject(new Error(`No deferred message catalog "${area}"`));
    p = load()
      .then((m) => {
        areaCatalogs[lang] = { ...areaCatalogs[lang], ...m.default };
        formatters.clear();
      })
      .catch((err: unknown) => {
        areaPending.delete(id);
        console.error(`Could not load the ${lang} messages of ${area}`, err);
      });
    areaPending.set(id, p);
  }
  return p;
}

/**
 * Load the messages of a deferred catalog area (English, the fallback, and the current language).
 * Awaited at the top level of the area's `messages.ts`, which every module using its keys imports.
 */
export function loadMessages(area: string): Promise<void> {
  requestedAreas.add(area);
  return Promise.all([loadArea(area, 'en'), ...(locale === 'en' ? [] : [loadArea(area, locale)])]).then(
    () => undefined,
  );
}

type Formatter = (vars?: MessageVars) => string;
/** Compiled formatters by language and key (cleared when messages or the time zone change). */
const formatters = new Map<string, Formatter>();
const warned = new Set<string>();

const STORAGE_KEY = 'dr.locale';

function detectLocale(): Locale {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    /* storage unavailable */
  }
  return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

let locale: Locale = detectLocale();
// Expression diagnostics, hovers and completions follow the member's language.
setExprLocale(locale);
let timeZone = browserTimeZone();
let numberFormat: NumberFormat = locale === 'de' ? 'de' : 'en';
let dateFormat: DateFormat = locale === 'de' ? 'dmy_dot' : 'mdy_slash';

/** Load the messages of a language (English is always loaded), with the deferred areas in use. */
export function ensureLocale(next: Locale): Promise<void> {
  const areas = [...requestedAreas].map((a) => loadArea(a, next));
  if (catalogs[next]) return Promise.all(areas).then(() => undefined);
  let p = pending.get(next);
  if (!p) {
    p = loaders[next as Exclude<Locale, 'en'>]()
      .then((m) => {
        catalogs[next] = m.default;
        formatters.clear();
      })
      .catch((err: unknown) => {
        pending.delete(next);
        console.error(`Could not load the ${next} messages`, err);
      });
    pending.set(next, p);
  }
  return Promise.all([p, ...areas]).then(() => undefined);
}

/** Resolves when the messages of the current language are loaded. */
export function localeReady(): Promise<void> {
  return ensureLocale(locale);
}

function syncDocument(): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

// The first screen renders in the right language: load it before any module uses `t`.
syncDocument();
await ensureLocale(locale);

// -- formatting ---------------------------------------------------------------------------------

function zoneFormats(zone: string) {
  const withZone = (styles: Record<string, Intl.DateTimeFormatOptions>) =>
    Object.fromEntries(Object.entries(styles).map(([k, v]) => [k, { ...v, timeZone: zone }]));
  return {
    date: withZone(IntlMessageFormat.formats.date),
    time: withZone(IntlMessageFormat.formats.time),
  };
}

/** A formatter for a compiled message in a language (exported for tests). */
export function compile(message: Compiled, lang: Locale): Formatter {
  if (typeof message === 'string') return () => message;
  if (Array.isArray(message)) {
    return (vars) =>
      message
        .map((part) => {
          if (typeof part === 'string') return part;
          const v = vars?.[part[0]];
          return v === undefined || v === null
            ? `{${part[0]}}`
            : v instanceof Date
              ? v.toISOString()
              : String(v);
        })
        .join('');
  }
  const fmt = new IntlMessageFormat(
    message.icu as ConstructorParameters<typeof IntlMessageFormat>[0],
    lang === 'de' ? 'de-DE' : 'en-US',
    zoneFormats(timeZone),
    { ignoreTag: true },
  );
  return (vars) => {
    try {
      return String(fmt.format(vars));
    } catch {
      return '';
    }
  };
}

function formatterOf(key: string): Formatter | null {
  const cacheKey = `${locale}\u0000${key}`;
  let f = formatters.get(cacheKey);
  if (f) return f;
  const own = messageOf(locale, key);
  const message = own ?? messageOf('en', key);
  if (message === undefined) return null;
  f = compile(message, own !== undefined ? locale : 'en');
  formatters.set(cacheKey, f);
  return f;
}

/** Is `K` a template-literal pattern (`dm.cat.${string}`) or `string`, rather than one key? */
type IsPattern<K extends string> = Record<never, never> extends Record<K, 1> ? true : false;

/**
 * A key argument: an exact message key, or a pattern / union of keys whose every member is a key
 * and that matches at least one key. Anything else is `never` (a type error at the call).
 */
export type KeyArg<K extends string> = K extends MessageKey
  ? K
  : IsPattern<K> extends true
    ? [Extract<MessageKey, K>] extends [never]
      ? never
      : K
    : never;

/** Placeholder values a key needs; optional free-form values for patterns. */
export type VarsArg<K extends string> = K extends keyof MessageParams
  ? [vars: MessageParams[K]]
  : K extends MessageKey
    ? [vars?: undefined]
    : [vars?: MessageVars];

/**
 * Translate a message: `t('shell.nav.home')`, `t('home.greeting', { name: 'Mara' })`,
 * `t('records.count', { count: 3 })`. Missing in the member's language → English; missing
 * everywhere → the key (warned once in development).
 */
export function t<const K extends string>(key: K & KeyArg<K>, ...vars: VarsArg<K>): string {
  return translate(key, vars[0]);
}

/**
 * A message whose key is built at run time (a finding's code, a status, an audit action): the catalogs'
 * parity test checks such keys, not the compiler. Prefer `t` wherever the key is known.
 */
export function tDynamic(key: string, vars?: MessageVars): string {
  return translate(key, vars);
}

function translate(key: string, vars: MessageVars | undefined): string {
  const f = formatterOf(key);
  if (!f) {
    if (import.meta.env.DEV && !warned.has(key)) {
      warned.add(key);
      console.warn(`[i18n] Missing message "${key}"`);
    }
    return key;
  }
  return f(vars);
}

/**
 * The message text in the current language without formatting, for templates that another
 * formatter fills later (`'vs. {previous}'` stored in a widget). Prefer `t` everywhere else.
 */
export function messageTemplate<const K extends MessageKey>(key: K): string {
  const message = messageOf(locale, key) ?? messageOf('en', key);
  if (message === undefined) return key;
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return message.map((p) => (typeof p === 'string' ? p : `{${p[0]}}`)).join('');
  return key;
}

/** Whether a message exists (in English, which every key has; deferred areas once loaded). */
export function hasMessage(key: string): key is MessageKey {
  return messageOf('en', key) !== undefined;
}

// -- language and formats -------------------------------------------------------------------

export function getLocale(): Locale {
  return locale;
}

/** BCP 47 tag for Intl formatters of the current language. */
export function intlLocale(): string {
  return locale === 'de' ? 'de-DE' : 'en-US';
}

/** IANA time zone dates and times are shown in (member's own, else the workspace's). */
export function getTimeZone(): string {
  return timeZone;
}

/** Workspace number format (Settings › General). */
export function getNumberFormat(): NumberFormat {
  return numberFormat;
}

/** Workspace date format for numeric dates (Settings › General). */
export function getDateFormat(): DateFormat {
  return dateFormat;
}

/** Switch the language (messages load in the background; await `localeReady()` before rendering). */
export function setLocale(next: Locale): void {
  locale = next;
  formatters.clear();
  setExprLocale(next);
  syncDocument();
  void ensureLocale(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Apply the signed-in member's effective language, time zone and formats. Returns true when the
 * language changed (strings already on screen need a re-render).
 */
export function applyFormats(formats: EffectiveFormatsDTO): boolean {
  let zone = formats.timezone;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
  } catch {
    zone = browserTimeZone();
  }
  if (zone !== timeZone) formatters.clear();
  timeZone = zone;
  numberFormat = formats.numberFormat;
  dateFormat = formats.dateFormat;
  if (formats.locale === locale) return false;
  setLocale(formats.locale);
  return true;
}

// -- labels inside sentences ----------------------------------------------------------------------

/**
 * A metadata label (object, field, option) placed inside a sentence: "New {object}". German keeps
 * nouns capitalised, so the label stays as written ("Neu: Kandidat"); English lower-cases plain
 * capitalised words ("New candidate", "Search close dates") but keeps acronyms and brand-like words
 * ("SLA policy", "LinkedIn profile"). Never call `toLowerCase()` on a label (plan §8.10).
 */
export function inSentence(label: string, lang: Locale = locale): string {
  return inSentenceFor(label, lang);
}

// -- keyboard shortcuts -------------------------------------------------------------------------

/** macOS and iPadOS show ⌘ ⌥ ⇧; everything else Ctrl/Strg, Alt and Shift/Umschalt. */
export function shortcutPlatform(): ShortcutPlatform {
  if (typeof navigator === 'undefined') return 'other';
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform || nav.platform || nav.userAgent;
  return /mac|iphone|ipad|ipod/i.test(platform) ? 'mac' : 'other';
}

/** A shortcut for the member's platform and language: `⇧⌘K`, `Ctrl+Shift+K`, `Strg+Umschalt+K`. */
export function formatShortcut(binding: string): string {
  return formatShortcutFor(binding, shortcutPlatform(), locale);
}

/** The keys of a shortcut for `<kbd>` rendering (`then` separates the steps of a sequence). */
export function shortcutKeys(binding: string): string[] {
  return shortcutKeysFor(binding, shortcutPlatform(), locale);
}
